import React, { useMemo, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS } from '@/api/endpoints';
import httpClient, { ApiResponse } from '@/api/httpClient';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { Customer } from '@/models/customer';
import { Lead } from '@/models/lead';
import { PaginatedResponse } from '@/api/types';
import { normalizeGlobalCustomerSearchQuery } from '@/utils/globalSearchQuery';

interface ImpersonationDialogProps {
    children: React.ReactNode;
}

export const ImpersonationDialog: React.FC<ImpersonationDialogProps> = ({ children }) => {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedRaw = useDebounce(searchQuery.trim(), 350);
    const debouncedQuery = useMemo(
        () => normalizeGlobalCustomerSearchQuery(debouncedRaw),
        [debouncedRaw]
    );

    const canViewCustomers = hasPermission(ModulePermission.CUSTOMERS, PermissionAction.VIEW);
    const canViewLeads = hasPermission(ModulePermission.LEADS, PermissionAction.VIEW);
    const shouldSearch = isOpen && debouncedQuery.length >= 2;

    const normalizedLiveQuery = useMemo(
        () => normalizeGlobalCustomerSearchQuery(searchQuery.trim()),
        [searchQuery]
    );
    const hasMinSearch = normalizedLiveQuery.length >= 2;

    const { data: customers = [], isLoading: isLoadingCustomers } = useQuery<Customer[]>({
        queryKey: ['global-search-customers', debouncedQuery],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<Customer[]>>(
                API_ENDPOINTS.CRM.CUSTOMERS.LIST,
                {
                    params: { search: debouncedQuery, per_page: 10 },
                }
            );
            return data.data ?? [];
        },
        enabled: canViewCustomers && shouldSearch,
    });

    const { data: leads = [], isLoading: isLoadingLeads } = useQuery<Lead[]>({
        queryKey: ['global-search-leads', debouncedQuery],
        queryFn: async () => {
            const { data } = await httpClient.get<PaginatedResponse<Lead>>(
                API_ENDPOINTS.CRM.LEADS,
                {
                    params: { search: debouncedQuery, per_page: 10 },
                }
            );
            return data.data ?? [];
        },
        enabled: canViewLeads && shouldSearch,
    });

    const isLoadingResults = isLoadingCustomers || isLoadingLeads;

    const results = useMemo(
        () => [
            ...customers.map(customer => ({
                id: customer.id,
                type: 'customer' as const,
                name: customer.name,
                email: customer.email,
                phone: customer.phone || customer.additional_phone_number || '',
            })),
            ...leads.map(lead => ({
                id: lead.id,
                type: 'lead' as const,
                name: lead.name,
                email: lead.email,
                phone: lead.phone || lead.additional_phone_number || '',
            })),
        ],
        [customers, leads]
    );

    const handleResultClick = (result: (typeof results)[number]) => {
        setIsOpen(false);
        setSearchQuery('');
        navigate(result.type === 'customer' ? `/customers/${result.id}` : `/leads/${result.id}`);
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Search className="h-5 w-5" />
                        Global Search
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <Input
                        autoFocus
                        placeholder="Search by name, email, or phone..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />

                    <div className="max-h-96 overflow-y-auto space-y-2">
                        {!hasMinSearch && (
                            <div className="text-center py-8 text-muted-foreground">
                                Type at least 2 characters to search customers and leads.
                            </div>
                        )}

                        {hasMinSearch && isLoadingResults && (
                            <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Searching...
                            </div>
                        )}

                        {hasMinSearch &&
                            !isLoadingResults &&
                            results.map(result => (
                                <button
                                    key={`${result.type}-${result.id}`}
                                    type="button"
                                    className="w-full p-3 border rounded-lg text-left transition-colors border-border hover:border-primary/50"
                                    onClick={() => handleResultClick(result)}
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">
                                                {result.name}
                                            </div>
                                            <div className="text-sm text-muted-foreground truncate">
                                                {result.email || '-'}
                                            </div>
                                            {result.phone && (
                                                <div className="text-xs text-muted-foreground truncate">
                                                    {result.phone}
                                                </div>
                                            )}
                                        </div>
                                        <Badge variant="outline" className="capitalize">
                                            {result.type}
                                        </Badge>
                                    </div>
                                </button>
                            ))}

                        {hasMinSearch && !isLoadingResults && results.length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">
                                No matching customers or leads found.
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
